import type { ZodType, ZodTypeDef } from "zod"

export function findFocusableAncestor(
  element: Element,
): HTMLElement | undefined {
  let ancestor: Element | undefined = element

  while (ancestor) {
    if (isHTMLElement(ancestor) && ancestor.tabIndex >= 0) break
    ancestor = ancestor.parentElement ?? undefined
  }

  return ancestor
}

// Convenient shortcut.
export function isElement(thing: unknown): thing is Element {
  return thing instanceof Element
}

// Convenient shortcut.
export function isHTMLElement(thing: unknown): thing is HTMLElement {
  return thing instanceof HTMLElement
}

export function getScrollableAncestor(element: Element): Element {
  let ancestor: Element | undefined = element

  while (ancestor && !isScrollable(ancestor)) {
    ancestor = ancestor.parentElement ?? undefined
  }

  // Fix for: Chrome's body returns an overflowY of "auto" even when it's html that scrolls.
  if (ancestor === document.body)
    ancestor = document.scrollingElement ?? undefined

  return ancestor ?? document.scrollingElement ?? document.documentElement
}

function isScrollable(element: Element): boolean {
  const style = getComputedStyle(element)
  return /auto|scroll/.test(style.overflow + style.overflowX + style.overflowY)
}

// Note that for this to work as intended with a scroller ancestor,
// that ancestor must not have position: "static" (usually "relative" will do nicely).
export function getOffsetTopRelativeTo(node: HTMLElement, ancestor: Element) {
  let result = 0
  let currentNode: HTMLElement | null = node

  while (currentNode && currentNode !== ancestor) {
    result += currentNode.offsetTop

    const parent: Element | null = currentNode.offsetParent
    currentNode = parent instanceof HTMLElement ? parent : null
  }

  return result
}

export function isScrolledToBottom(scroller: Element, threshold = 50) {
  const distanceToBottom =
    scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight

  return distanceToBottom < threshold
}

/**
 * Returns whether the element is partially visible within the currently scrolled viewport.
 */
export function isElementInView(element: Element) {
  const rect = element.getBoundingClientRect()

  const inViewVertically =
    rect.top + rect.height >= 0 && rect.top <= window.innerHeight

  const inViewHorizontally =
    rect.left + rect.width >= 0 && rect.left <= window.innerWidth

  return inViewVertically && inViewHorizontally
}

export const localStorage = {
  isAvailable: () => {
    const test = "__$$__test"

    try {
      window.localStorage.setItem(test, test)
      window.localStorage.removeItem(test)
      return true
    } catch (_) {
      return false
    }
  },

  has: (key: string) => {
    return Boolean(localStorage.read(key))
  },

  read: (key: string): string | undefined => {
    try {
      return window.localStorage.getItem(key) ?? undefined
    } catch {
      return undefined
    }
  },

  write: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value)
      // This event is normally dispatched when ANOTHER tab makes a modification to localStorage.
      // This normalizes the event into being dispatched in the current tab as well.
      window.dispatchEvent(
        new StorageEvent("storage", { key, newValue: value }),
      )
    } catch {
      // Ignored. Best effort.
    }
  },

  remove: (key: string) => {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // Ignored. Best effort.
    }
  },

  readJSON: <Value>(
    key: string,
    validator: ZodType<Value, ZodTypeDef, unknown>,
  ): Value | undefined => {
    try {
      const json = localStorage.read(key)
      if (!json) return undefined

      const parsed: unknown = JSON.parse(json)
      const validated = validator.safeParse(parsed)

      if (!validated.success) return undefined

      return validated.data
    } catch {
      return undefined
    }
  },

  subscribe: (listener: () => void) => {
    window.addEventListener("storage", listener)
    return () => window.removeEventListener("storage", listener)
  },
}
